const chapter = {
  id: 'anatomy-flowering-plants',
  slug: 'anatomy-flowering-plants',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Anatomy of Flowering Plants',
        hindi: 'पुष्पीय पादपों की शारीरिकी',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3 questions in NEET',
        hindi: 'NEET में 3 प्रश्न दो व्यापक प्रकार: **मेरिस्टेमेटिक** (विभाजन) + **स्थायी** (कोई विभाजन नहीं)। **मेरिस्टेम्स**:',
  },
  syllabusTags: ['Structural Organisation', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'tissues',
      title: {
        english: 'Plant Tissues',
        hindi: 'पादप ऊतक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Two broad types: **Meristematic** (dividing) + **Permanent** (no division).',
        hindi: 'दो व्यापक प्रकार: **मेरिस्टेमेटिक** (विभाजन) + **स्थायी** (कोई विभाजन नहीं)।',
            },
            {
              english: '**Meristems**:',
        hindi: '**विभज्योतक**:',
            },
            {
              english: '• **Apical** — root + shoot tip → primary growth.',
        hindi: '• **एपिकल** — जड़ + प्ररोह टिप → प्राथमिक वृद्धि।',
            },
            {
              english: '• **Intercalary** — at base of leaves/internodes (grasses) → primary growth.',
        hindi: '• **इंटरकैलेरी** - leaves/internodes (घास) → प्राथमिक विकास के आधार पर।',
            },
            {
              english: '• **Lateral** — fascicular + interfascicular cambium, cork cambium → **secondary growth** (girth).',
        hindi: '• **पार्श्व** - प्रावरणी + इंटरफैसिकुलर कैंबियम, कॉर्क कैंबियम → **द्वितीयक वृद्धि** (परिधि)।',
            },
            {
              english: '**Permanent tissues**:',
        hindi: '**स्थायी ऊतक**:',
            },
            {
              english: '• **Simple** — Parenchyma (living, thin-walled, storage); Collenchyma (living, thickened at corners with cellulose+pectin, mechanical); Sclerenchyma (dead, lignified — fibres + sclereids).',
        hindi: '• **सरल** — पैरेन्काइमा (जीवित, पतली दीवार वाली, भंडारण); कोलेनकाइमा (जीवित, सेलूलोज़ + पेक्टिन के साथ कोनों पर गाढ़ा, यांत्रिक); स्क्लेरेन्काइमा (मृत, लिग्निफाइड - फाइबर + स्केलेरिड्स)।',
            },
            {
              english: '• **Complex** — **Xylem** (tracheids, vessels, xylem fibres, xylem parenchyma) — water conduction; **Phloem** (sieve tubes, companion cells, phloem fibres, phloem parenchyma) — food translocation. Companion cells absent in pteridophytes & gymnosperms.',
        hindi: '• **कॉम्प्लेक्स** - **जाइलम** (ट्रेचिड, वाहिकाएं, जाइलम फाइबर, जाइलम पैरेन्काइमा) - जल चालन; **फ्लोएम** (छलनी नलिकाएं, साथी कोशिकाएं, फ्लोएम फाइबर, फ्लोएम पैरेन्काइमा) - भोजन स्थानांतरण। टेरिडोफाइट्स और जिम्नोस्पर्म में सहयोगी कोशिकाएँ अनुपस्थित होती हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'tissue-system',
      title: {
        english: 'Tissue Systems',
        hindi: 'ऊतक तंत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Three tissue systems (Sachs, 1875):',
        hindi: 'तीन ऊतक प्रणालियाँ (सैक्स, 1875):',
            },
            {
              english: '• **Epidermal** — epidermis + stomata + epidermal appendages (root hairs, trichomes). Stomata = guard cells (bean-shaped in dicots, dumbbell in monocots/grasses) + subsidiary cells.',
        hindi: '• **एपिडर्मल** - एपिडर्मिस + स्टोमेटा + एपिडर्मल उपांग (जड़ बाल, ट्राइकोम)। Stomata = guard cells (bean-shaped in dicots, monocots/grasses में डम्बल) + सहायक कोशिकाएँ।',
            },
            {
              english: '• **Ground** — all tissues except epidermis & vascular = parenchyma, collenchyma, sclerenchyma. **Cortex, pericycle, pith, medullary rays**.',
        hindi: '• **ग्राउंड** - एपिडर्मिस और vascular = parenchyma, कोलेनकाइमा, स्क्लेरेनकाइमा को छोड़कर सभी ऊतक। **कॉर्टेक्स, पेरीसाइकिल, मज्जा, मज्जा किरणें**।',
            },
            {
              english: '• **Vascular** — xylem + phloem in **vascular bundles**. Conjoint (xylem + phloem on same radius) + Collateral (open in dicot stems with cambium; closed in monocots without cambium).',
        hindi: '• **संवहनी** - **संवहनी बंडलों** में जाइलम + फ्लोएम। संयुक्त (जाइलम + फ्लोएम एक ही त्रिज्या पर) + संपार्श्विक (कैम्बियम के साथ द्विबीजपत्री तनों में खुला; कैम्बियम के बिना मोनोकोट में बंद)।',
            },
            {
              english: 'Radial bundles → roots (xylem & phloem on different radii, alternate).',
        hindi: 'रेडियल बंडल → जड़ें (विभिन्न रेडी पर जाइलम और फ्लोएम, वैकल्पिक)।',
            },
          ],
        },
      ],
    },
    {
      id: 'anatomy-organs',
      title: {
        english: 'Anatomy of Dicot vs Monocot Root, Stem, Leaf',
        hindi: 'द्विबीजपत्री बनाम एकबीजपत्री जड़, तना, पत्ती की शारीरिकी',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Dicot', 'Monocot'],
          rows: [
            ['Root xylem (number)', '2-6 (di-tetrarch usually)', 'Polyarch (> 6)'],
            ['Root pith', 'Small / absent', 'Large, well-developed'],
            ['Root cambium', 'Develops later → secondary growth', 'Absent'],
            ['Stem hypodermis', 'Collenchymatous', 'Sclerenchymatous'],
            ['Stem vascular bundles', 'Open (with cambium); arranged in ring', 'Closed (no cambium); scattered'],
            ['Stem bundle sheath', 'Absent', 'Present (sclerenchymatous)'],
            ['Stem secondary growth', 'Common', 'Absent'],
            ['Leaf', 'Dorsiventral (palisade upper, spongy lower)', 'Isobilateral (mesophyll uniform)'],
            ['Leaf bulliform cells', 'Absent', 'Present (in grasses; for rolling)'],
            ['Stomata', 'Mainly lower side', 'Both sides equal'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Dicot root cross-section (e.g. *Helianthus*) — **epiblema → cortex → endodermis (with Casparian strips) → pericycle → vascular bundles (radial, 2-4) → small pith**.',
        hindi: 'द्विबीजपत्री जड़ अनुप्रस्थ काट (उदा. *हेलियनथस*) - **एपिब्लेमा → कॉर्टेक्स → एंडोडर्मिस (कैस्पेरियन स्ट्रिप्स के साथ) → पेरीसाइकल → संवहनी बंडल (रेडियल, 2-4) → छोटा मज्जा**।',
            },
            {
              english: 'Monocot root (e.g. maize) — same arrangement but **polyarch (> 6 xylem patches), large pith, no secondary growth**.',
        hindi: 'मोनोकॉट जड़ (जैसे मक्का) - समान व्यवस्था लेकिन **पॉलीआर्क (> 6 जाइलम पैच), बड़ा गूदा, कोई द्वितीयक वृद्धि नहीं**।',
            },
            {
              english: 'Dicot stem — epidermis → hypodermis (collenchyma) → general cortex → endodermis (starch sheath) → pericycle → **ring of vascular bundles (open, conjoint, collateral)** → pith.',
        hindi: 'द्विबीजपत्री तना - एपिडर्मिस → हाइपोडर्मिस (कोलेंकाइमा) → सामान्य कॉर्टेक्स → एंडोडर्मिस (स्टार्च शीथ) → पेरीसाइकल → **संवहनी बंडलों की अंगूठी (खुला, संयुक्त, संपार्श्विक)** → मज्जा।',
            },
            {
              english: 'Monocot stem — epidermis → hypodermis (sclerenchyma) → ground tissue → **scattered closed vascular bundles**, larger near periphery, smaller in centre.',
        hindi: 'मोनोकॉट स्टेम - एपिडर्मिस → हाइपोडर्मिस (स्क्लेरेन्काइमा) → ग्राउंड टिश्यू → **बिखरे हुए बंद संवहनी बंडल**, परिधि के पास बड़े, केंद्र में छोटे।',
            },
            {
              english: 'Dicot leaf — epidermis (upper + lower) → mesophyll (palisade + spongy) → vascular bundles (xylem above, phloem below).',
        hindi: 'द्विबीजपत्री पत्ती - एपिडर्मिस (ऊपरी + निचला) → मेसोफिल (पैलिसेड + स्पंजी) → संवहनी बंडल (ऊपर जाइलम, नीचे फ्लोएम)। ***पार्श्व विभज्योतक** - संवहनी कैम्बियम + कॉर्क कैम्बियम की गतिविधि से परिधि में वृद्धि। द्विबीजपत्री तने में: **इंटरफैसिकुलर कैंबियम** + **फासीकुलर कैंबियम** जुड़कर → पूर्ण **कैंबियल रिंग** बनाते हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'secondary-growth',
      title: {
        english: 'Secondary Growth',
        hindi: 'द्वितीयक वृद्धि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Increase in girth by activity of **lateral meristems** — vascular cambium + cork cambium.',
        hindi: '**पार्श्व विभज्योतक** की गतिविधि द्वारा परिधि में वृद्धि - संवहनी कैम्बियम + कॉर्क कैम्बियम।',
            },
            {
              english: 'In dicot stem: **interfascicular cambium** + **fascicular cambium** join → form complete **cambial ring**.',
        hindi: 'द्विबीजपत्री तने में: **इंटरफैसिक्युलर कैम्बियम** + **फासीकुलर कैम्बियम** जुड़कर → पूर्ण **कैम्बियल रिंग** बनाते हैं।',
            },
            {
              english: 'Cambium cuts off cells: **inside → secondary xylem (wood)**; outside → secondary phloem.',
        hindi: 'कैम्बियम कोशिकाओं को काटता है: **अंदर → द्वितीयक जाइलम (लकड़ी)**; बाहर → द्वितीयक फ्लोएम।',
            },
            {
              english: '**Secondary xylem ≫ secondary phloem** → wood accumulates.',
        hindi: '**द्वितीयक जाइलम ≫ द्वितीयक फ्लोएम** → लकड़ी जमा होती है।',
            },
            {
              english: '**Spring wood (early wood)** — light, low density, wider vessels; **Autumn wood (late wood)** — narrow, darker → annual rings.',
        hindi: '**वसंत की लकड़ी (शुरुआती लकड़ी)** - हल्की, कम घनत्व, चौड़े बर्तन; **शरद ऋतु की लकड़ी (देर की लकड़ी)** - संकीर्ण, गहरा → वार्षिक छल्ले।',
            },
            {
              english: '**Heartwood** (dead, dark, durable, no conduction) + **Sapwood** (light, conducts).',
        hindi: '**हार्टवुड** (मृत, अंधेरा, टिकाऊ, कोई चालन नहीं) + **सैपवुड** (प्रकाश, संचालन)।',
            },
            {
              english: '**Cork cambium / phellogen** → produces **cork (phellem)** outside + **secondary cortex (phelloderm)** inside.',
        hindi: '**कॉर्क कैम्बियम / फेलोजेन** → **कॉर्क (पेलेम)** बाहर + **सेकेंडरी कॉर्टेक्स (पेलोडर्म)** अंदर पैदा करता है।',
            },
            {
              english: '**Periderm** = phellem + phellogen + phelloderm. **Lenticels** in cork allow gas exchange.',
        hindi: '**पेरीडर्म** = फेलेम + फेलोजेन + फेलोडर्म। कॉर्क में **लेन्टिसेल्स** गैस विनिमय की अनुमति देते हैं।',
            },
            {
              english: '**Bark** = all tissues outside vascular cambium (technical); in older stems = scaly periderm.',
        hindi: '**छाल** = संवहनी कैम्बियम के बाहर के सभी ऊतक (तकनीकी); पुराने stems = scaly periderm में।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Apical + Intercalary} \\Rightarrow \\text{primary growth; Lateral} \\Rightarrow \\text{secondary}',
      desc: {
        english: '',
        hindi: 'एपिकल + इंटरकैलेरी प्राइमरी ग्रोथ; पार्श्व माध्यमिक',
      },
    },
    {
      latex: '\\text{Xylem: tracheids, vessels, fibres, parenchyma}',
      desc: {
        english: '',
        hindi: 'जाइलम: वाहिकाएँ, वाहिकाएँ, तंतु, पैरेन्काइमा',
      },
    },
    {
      latex: '\\text{Phloem: sieve tubes, companion cells, fibres, parenchyma}',
      desc: {
        english: '',
        hindi: 'फ्लोएम: छलनी नलिकाएं, साथी कोशिकाएं, फाइबर, पैरेन्काइमा',
      },
    },
    {
      latex: '\\text{Dicot stem: open VB, in ring; Monocot stem: closed, scattered}',
      desc: {
        english: '',
        hindi: 'द्विबीजपत्री तना: खुला वीबी, रिंग में; मोनोकॉट तना: बंद, बिखरा हुआ',
      },
    },
    {
      latex: '\\text{Dicot root xylem 2-6 (di-tetrarch); Monocot polyarch}',
      desc: {
        english: '',
        hindi: 'द्विबीजपत्री जड़ जाइलम 2-6 (डाई-टेट्रार्क); मोनोकोट पॉलीआर्क',
      },
    },
    {
      latex: '\\text{Dicot leaf: dorsiventral (palisade + spongy); Monocot: isobilateral}',
      desc: {
        english: '',
        hindi: 'द्विबीजपत्री पत्ता: डॉर्सिवेंट्रल (पलिसडे + स्पंजी); मोनोकॉट: समद्विपक्षीय',
      },
    },
    {
      latex: '\\text{Periderm = phellem + phellogen + phelloderm; Lenticels for gas exchange}',
      desc: {
        english: '',
        hindi: 'पेरिडर्म = फेलेम + फेलोजेन + फेलोडर्म; गैस विनिमय के लिए मसूर की दाल',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Anatomy',
      problem: {
        english: 'Vascular bundles in monocot stem are:',
        hindi: 'मोनोकॉट तने में संवहनी बंडल हैं:',
      },
      answer: {
        english: 'Closed, scattered (no cambium)',
        hindi: 'बंद, बिखरा हुआ (कोई कैम्बियम नहीं)',
      },
      solution: {
        english: 'No secondary growth.',
        hindi: 'कोई द्वितीयक वृद्धि नहीं।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Cambium',
      problem: {
        english: 'Cambium produces secondary phloem on:',
        hindi: 'कैम्बियम द्वितीयक फ्लोएम का निर्माण करता है:',
      },
      answer: {
        english: 'Outer side',
        hindi: 'बाहरी तरफ',
      },
      solution: {
        english: 'Secondary xylem on inner side; xylem ≫ phloem.',
        hindi: 'भीतरी तरफ द्वितीयक जाइलम; जाइलम ≫ फ्लोएम.',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Stomata',
      problem: {
        english: 'Dumbbell-shaped guard cells are seen in:',
        hindi: 'डम्बल के आकार की गार्ड कोशिकाएँ देखी जाती हैं:',
      },
      answer: {
        english: 'Grasses (monocots)',
        hindi: 'घास (मोनोकोट)',
      },
      solution: {
        english: 'Bean-shaped in most dicots.',
        hindi: 'अधिकांश द्विबीजपत्री में बीन के आकार का।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Casparian',
      problem: {
        english: 'Casparian strips occur in:',
        hindi: 'कैस्पेरियन स्ट्रिप्स होती हैं:',
      },
      answer: {
        english: 'Endodermis of root',
        hindi: 'जड़ की अंतःत्वचा',
      },
      solution: {
        english: 'Suberin deposit; controls water entry.',
        hindi: 'सुबेरिन जमा; जल प्रवेश को नियंत्रित करता है।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Wood',
      problem: {
        english: 'Heartwood differs from sapwood in:',
        hindi: 'हार्टवुड सैपवुड से भिन्न है:',
      },
      answer: {
        english: 'Heartwood is dark, dead, non-conducting',
        hindi: 'हार्टवुड काला, मृत, निष्क्रिय है',
      },
      solution: {
        english: 'Tyloses block vessels.',
        hindi: 'टायलोज़ वाहिकाओं को अवरुद्ध करता है।',
      },
    },
  ],
  next: {
    slug: 'structural-organisation-animals',
    title: 'Structural Organisation in Animals',
  },
};

export default chapter;
